import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SupportedLanguage } from "../../app";
import {
  BlockingCategorization,
  CategorizationState,
  CategorizationAnnotation,
  CategorizationAnnotationRemove,
  CategorizationAnnotationRemoveByCategorizations,
  CategorizationAnnotationRemoveByGroupBlockIndex,
  UpdateMultiGroupBlockCategorizationAnnotation,
} from "../../configMap/interfaces/category";
import { BlockingEntity } from "../../configMap/interfaces/entity";
import { equals } from "../utils/categorizationAnnotation";
import {
  DerivedCategorizationOptionsPayload,
  KeyByOptions,
} from "../interfaces/categorizationEvents";

interface MetadataState {
  categorizationState: CategorizationState;
  incompleteCategorizations: Array<BlockingCategorization>;
  incompleteEntities: Array<BlockingEntity>;
  derivedCategorizationOptions: KeyByOptions;
  loadingCategorizationIds: Array<string>;
  language: SupportedLanguage;
}

const initialState: MetadataState = {
  categorizationState: {
    categorizationAnnotations: [],
  },
  incompleteCategorizations: [],
  incompleteEntities: [],
  derivedCategorizationOptions: {},
  loadingCategorizationIds: [],
  language: "en",
};

export const metadataSlice = createSlice({
  name: "metadata",
  initialState,
  reducers: {
    setMetadataLanguage: (state, action: PayloadAction<SupportedLanguage>) => {
      state.language = action.payload;
    },
    setCategorizationAnnotationsBatch: (
      state,
      action: PayloadAction<Array<CategorizationAnnotation>>,
    ) => {
      state.categorizationState.categorizationAnnotations = action.payload;
    },
    updateCategorizationAnnotation: (
      state,
      action: PayloadAction<CategorizationAnnotation>,
    ) => {
      const { payload } = action;

      const found = state.categorizationState.categorizationAnnotations.find(
        (ca) => equals(ca, payload),
      );

      if (found) {
        state.categorizationState.categorizationAnnotations =
          state.categorizationState.categorizationAnnotations?.map((ca) => {
            if (equals(ca, payload)) {
              return payload;
            }

            return ca;
          });
        return;
      }

      state.categorizationState.categorizationAnnotations = [
        ...state.categorizationState.categorizationAnnotations,
        payload,
      ];
    },
    removeCategorizationAnnotation: (
      state,
      action: PayloadAction<CategorizationAnnotationRemove>,
    ) => {
      const { payload } = action;

      const found = state.categorizationState.categorizationAnnotations.find(
        (ca) => equals(ca, payload),
      );

      if (found) {
        state.categorizationState.categorizationAnnotations =
          state.categorizationState.categorizationAnnotations.filter((ca) => {
            return !equals(ca, payload);
          });
      }
    },
    modifyCategorizationAnnotationsByGroupBlockIndexInState: (
      state,
      action: PayloadAction<CategorizationAnnotationRemoveByGroupBlockIndex>,
    ) => {
      const {
        documentId,
        categorizations,
        groupBlockIndex,
        isGroupBlockRemoved,
      } = action.payload;
      const categorizationIds = categorizations.map(
        (categorization) => categorization.id,
      );

      state.categorizationState.categorizationAnnotations =
        state.categorizationState.categorizationAnnotations
          .filter(
            (ca) =>
              !(
                ca.documentId === documentId &&
                categorizationIds.includes(ca.categorizationId) &&
                ca.index === groupBlockIndex
              ),
          )
          .map((ca) => {
            if (!isGroupBlockRemoved) {
              return ca;
            }

            if (
              ca.documentId === documentId &&
              categorizationIds.includes(ca.categorizationId) &&
              ca.index! > groupBlockIndex
            ) {
              return {
                ...ca,
                index: ca.index! - 1,
              };
            }
            return ca;
          });
    },
    removeCategorizationAnnotationsByCategorizationsInState: (
      state,
      action: PayloadAction<CategorizationAnnotationRemoveByCategorizations>,
    ) => {
      const { documentId, categorizations } = action.payload;
      const categorizationIds = categorizations.map(
        (categorization) => categorization.id,
      );

      state.categorizationState.categorizationAnnotations =
        state.categorizationState.categorizationAnnotations.filter(
          (ca) =>
            !(
              ca.documentId === documentId &&
              categorizationIds.includes(ca.categorizationId)
            ),
        );
    },
    updateGroupBlockCategorizationAnnotationsInState: (
      state,
      action: PayloadAction<UpdateMultiGroupBlockCategorizationAnnotation>,
    ) => {
      const { groupBlockIndex, categorizations, addBefore } = action.payload;
      const categorizationIds = categorizations.map(
        (categorization) => categorization.id,
      );

      state.categorizationState.categorizationAnnotations =
        state.categorizationState.categorizationAnnotations.map((ca) => {
          if (!ca.index) {
            return ca;
          }
          if (
            addBefore &&
            ca.index - 1 === 0 &&
            categorizationIds.includes(ca.categorizationId)
          ) {
            return { ...ca, index: ca.index + 1 };
          }
          if (
            ca.index - 1 > groupBlockIndex &&
            categorizationIds.includes(ca.categorizationId)
          ) {
            return { ...ca, index: ca.index + 1 };
          }
          return ca;
        });
    },
    setIncompleteCategorizations: (
      state,
      action: PayloadAction<Array<BlockingCategorization>>,
    ) => {
      state.incompleteCategorizations = action.payload;
    },
    setDerivedCategorizationOptions: (
      state,
      action: PayloadAction<DerivedCategorizationOptionsPayload>,
    ) => {
      const { categorizationId, options } = action.payload;
      state.derivedCategorizationOptions[categorizationId] = options;
    },
    addLoadingCategorizationIds: (
      state,
      action: PayloadAction<Array<string>>,
    ) => {
      state.loadingCategorizationIds = [
        ...state.loadingCategorizationIds,
        ...action.payload,
      ];
    },
    removeLoadingCategorizationIds: (
      state,
      action: PayloadAction<Array<string>>,
    ) => {
      state.loadingCategorizationIds = state.loadingCategorizationIds.filter(
        (c) => {
          return !action.payload.includes(c);
        },
      );
    },
    removeIncompleteCategorization: (
      state,
      action: PayloadAction<BlockingCategorization>,
    ) => {
      const payload = action.payload;

      state.incompleteCategorizations = state.incompleteCategorizations.filter(
        (bc) => {
          return !(
            bc.categorizationId === payload.categorizationId &&
            bc.groupBlockId === payload.groupBlockId &&
            bc.index === payload.index
          );
        },
      );
    },
    setIncompleteEntities: (
      state,
      action: PayloadAction<Array<BlockingEntity>>,
    ) => {
      state.incompleteEntities = action.payload;
    },
    removeIncompleteEntity: (state, action: PayloadAction<BlockingEntity>) => {
      const payload = action.payload;

      state.incompleteEntities = state.incompleteEntities.filter((be) => {
        return !(
          be.entityTypeId === payload.entityTypeId &&
          be.groupBlockId === payload.groupBlockId &&
          be.index === payload.index
        );
      });
    },
    resetCategorizationAnnotationsInState: (state) => {
      state.categorizationState.categorizationAnnotations = [];
    },
    resetMetadataReducer: () => initialState,
  },
});

export const {
  setMetadataLanguage,
  resetMetadataReducer,
  updateCategorizationAnnotation,
  removeCategorizationAnnotation,
  modifyCategorizationAnnotationsByGroupBlockIndexInState,
  removeCategorizationAnnotationsByCategorizationsInState,
  updateGroupBlockCategorizationAnnotationsInState,
  resetCategorizationAnnotationsInState,
  setCategorizationAnnotationsBatch,
  setIncompleteCategorizations,
  setIncompleteEntities,
  removeIncompleteEntity,
  setDerivedCategorizationOptions,
  addLoadingCategorizationIds,
  removeLoadingCategorizationIds,
  removeIncompleteCategorization,
} = metadataSlice.actions;

export default metadataSlice.reducer;
export class removeDocumentCategoryAnnotation {}
